import { useCallback, useEffect, useRef, useState } from "react";
import {
  DatasetError,
  getDataset,
  queryRows,
  undoLastChange,
  writeRows,
  type DatasetDescription,
  type DatasetField,
  type DatasetOp,
  type DatasetRow,
  type DatasetValue,
  type DatasetWriteResult,
} from "@/api/datasets";
import { translate } from "@/i18n";
import { framesDatasetMessages } from "@/i18n/messages/frames-dataset";

/** Rows per page of the opened table. */
export const DATASET_PAGE_SIZE = 50;

/** The parameterless notices. */
type Notice =
  | "conflict"
  | "saved"
  | "saveFailed"
  | "rowAdded"
  | "rowDeleted"
  | "undone"
  | "nothingToUndo"
  | "undoRefused"
  | "tableFull";

export type DatasetEditOutcome = "saved" | "conflict" | "invalid";

export interface DatasetCellRef {
  rowId: string;
  fieldId: string;
}

interface PageState {
  status: "loading" | "ready" | "error";
  fields: DatasetField[];
  /** The revision of `fields`; a field change sends it back. */
  schemaRevision: number;
  rows: DatasetRow[];
  total: number;
}

const say = (key: Notice) => translate(framesDatasetMessages, key);

/**
 * One page of a Data frame's rows and the person's edits to it. The page
 * reloads when the frame's `dataRevision` (from `frame.updated`) rises above
 * the revision it shows; its own writes apply their response and raise that
 * revision, so they cause no reload (unless another writer came between).
 * Edits to one row are sent one after another, each with the revision the
 * one before returned. `notice` is the latest save or refusal, for the
 * table's live region (`noticeId` lets a repeated text be announced again).
 * `noticeUndoable`: the notice is "Row deleted" and `undoDelete` would still
 * take back that delete (no write this window has heard of came after it).
 * Every Undo names the table revision it means to take back, so the server
 * refuses it once a newer write landed. One Undo runs at a time.
 */
export function useDatasetPage({
  projectId,
  frameId,
  dataRevision,
  schemaRevision,
}: {
  projectId: string | null;
  frameId: string;
  dataRevision: number;
  schemaRevision: number;
}) {
  const [offset, setOffset] = useState(0);
  const [attempt, setAttempt] = useState(0);
  const [page, setPage] = useState<PageState>({
    status: "loading",
    fields: [],
    schemaRevision: -1,
    rows: [],
    total: 0,
  });
  const [notice, setNotice] = useState<{
    text: string;
    id: number;
    /** The revision a delete left the table at, while Undo would take it back. */
    undoRevision?: number;
  } | null>(null);
  const [conflict, setConflict] = useState<DatasetCellRef | null>(null);
  // What the page shows (-1 until loaded), what the frame last announced,
  // and the shown rows: refs, so writes and reloads agree between renders.
  const shown = useRef({ data: -1, schema: -1 });
  const announced = useRef({ data: dataRevision, schema: schemaRevision });
  const rowsRef = useRef<DatasetRow[]>([]);
  const shownOffset = useRef<number | null>(null);
  const queues = useRef(new Map<string, Promise<unknown>>());
  const sequence = useRef(0);
  // An Undo in flight: a second press would take back an older change too.
  const undoing = useRef(false);

  const tell = (key: Notice | { text: string }, undoRevision?: number) =>
    setNotice((previous) => ({
      text: typeof key === "string" ? say(key) : key.text,
      id: (previous?.id ?? 0) + 1,
      undoRevision,
    }));

  const setRows = (rows: DatasetRow[], totalChange = 0) => {
    rowsRef.current = rows;
    setPage((previous) => ({
      ...previous,
      rows,
      total: previous.total + totalChange,
    }));
  };

  const load = useCallback(
    // `followUp`: this load may ask once more (never a loop).
    async (withFields: boolean, followUp = true): Promise<void> => {
      if (!projectId) return;
      const mine = ++sequence.current;
      try {
        const [description, result] = await Promise.all([
          withFields ? getDataset(projectId, frameId) : null,
          queryRows(projectId, frameId, { limit: DATASET_PAGE_SIZE, offset }),
        ]);
        if (mine !== sequence.current) return;
        // Older than a write already shown: ask again, once.
        if (result.data_revision < shown.current.data) {
          return followUp ? load(withFields, false) : undefined;
        }
        shown.current = {
          data: result.data_revision,
          schema: description?.schema_revision ?? shown.current.schema,
        };
        rowsRef.current = result.rows;
        shownOffset.current = offset;
        setPage((previous) => ({
          status: "ready",
          fields: description?.fields ?? previous.fields,
          schemaRevision: shown.current.schema,
          rows: result.rows,
          total: result.total,
        }));
        // A change announced while this page was on its way.
        const schemaChanged = announced.current.schema > shown.current.schema;
        if (
          followUp &&
          (schemaChanged || announced.current.data > shown.current.data)
        ) {
          return load(schemaChanged, false);
        }
      } catch {
        if (mine !== sequence.current) return;
        // A failed refresh keeps the page shown; a failed page is an error.
        if (shownOffset.current !== offset) {
          setPage((previous) => ({ ...previous, status: "error" }));
        }
      }
    },
    [frameId, offset, projectId],
  );

  // The page itself: first load, another page, Try again.
  useEffect(() => {
    void load(shown.current.schema < 0);
  }, [load, attempt]);

  // A change elsewhere (an agent, another window) raised the revision.
  useEffect(() => {
    announced.current = { data: dataRevision, schema: schemaRevision };
    if (shown.current.data < 0) return;
    const schemaChanged = schemaRevision > shown.current.schema;
    if (schemaChanged || dataRevision > shown.current.data) {
      void load(schemaChanged);
    }
  }, [dataRevision, schemaRevision, load]);

  const write = async (ops: DatasetOp[]): Promise<DatasetWriteResult> => {
    if (!projectId) throw new DatasetError(null);
    const result = await writeRows(projectId, frameId, ops);
    const next = shown.current.data + 1;
    shown.current.data = Math.max(shown.current.data, result.data_revision);
    // Another writer came between: this page misses its change.
    if (result.data_revision !== next) void load(false);
    return result;
  };

  const replaceRow = (rowId: string, next: DatasetRow | null) =>
    setRows(
      next
        ? rowsRef.current.map((r) => (r.id === rowId ? next : r))
        : rowsRef.current.filter((r) => r.id !== rowId),
      next ? 0 : -1,
    );

  const setValue = (rowId: string, fieldId: string, value: DatasetValue) => {
    const row = rowsRef.current.find((r) => r.id === rowId);
    if (row)
      replaceRow(rowId, {
        ...row,
        values: { ...row.values, [fieldId]: value },
      });
  };

  /** Runs `task` after the row's earlier writes, so it sends their revision. */
  const queued = <T>(rowId: string, task: () => Promise<T>): Promise<T> => {
    const run = (queues.current.get(rowId) ?? Promise.resolve()).then(task);
    queues.current.set(
      rowId,
      run.catch(() => undefined),
    );
    return run;
  };

  /** A refused write: a conflict shows the current row, anything else says why. */
  const refused = (error: unknown, rowId: string, fieldId?: string) => {
    const refusal =
      error instanceof DatasetError ? error : new DatasetError(error);
    if (refusal.code === "conflict" && refusal.row !== undefined) {
      replaceRow(rowId, refusal.row);
      setConflict(fieldId ? { rowId, fieldId } : null);
      tell("conflict");
      return "conflict" as const;
    }
    if (refusal.code === "table_full") tell("tableFull");
    else if (refusal.code === "invalid") tell({ text: refusal.message });
    else tell("saveFailed");
    return "invalid" as const;
  };

  const edit = (
    rowId: string,
    fieldId: string,
    value: DatasetValue,
  ): Promise<DatasetEditOutcome> => {
    const before = rowsRef.current.find((r) => r.id === rowId);
    if (!before) return Promise.resolve("invalid");
    const previous = before.values[fieldId] ?? null;
    setConflict(null);
    // Shown at once; the server's row replaces it.
    setValue(rowId, fieldId, value);
    return queued(rowId, async () => {
      const row = rowsRef.current.find((r) => r.id === rowId);
      if (!row) return "invalid";
      try {
        const result = await write([
          {
            op: "update",
            id: rowId,
            expected_revision: row.revision,
            values: { [fieldId]: value },
          },
        ]);
        if (result.rows[0]) replaceRow(rowId, result.rows[0]);
        tell("saved");
        return "saved";
      } catch (error) {
        setValue(rowId, fieldId, previous);
        return refused(error, rowId, fieldId);
      }
    });
  };

  /** A new empty row at the table's end. */
  const addRow = async () => {
    try {
      const result = await write([
        { op: "insert", id: crypto.randomUUID(), values: {} },
      ]);
      const added = result.rows[0];
      if (!added) return;
      const lastOffset =
        Math.floor(page.total / DATASET_PAGE_SIZE) * DATASET_PAGE_SIZE;
      if (lastOffset === offset) setRows([...rowsRef.current, added], 1);
      else setOffset(lastOffset);
      tell("rowAdded");
    } catch (error) {
      refused(error, "");
    }
  };

  const deleteRow = (rowId: string) =>
    queued(rowId, async () => {
      const row = rowsRef.current.find((r) => r.id === rowId);
      if (!row) return;
      try {
        const result = await write([
          { op: "delete", id: rowId, expected_revision: row.revision },
        ]);
        replaceRow(rowId, null);
        tell("rowDeleted", result.data_revision);
        // The page's last row: show the page before it.
        if (rowsRef.current.length === 0 && offset > 0) {
          setOffset(Math.max(0, offset - DATASET_PAGE_SIZE));
        }
      } catch (error) {
        refused(error, rowId);
      }
    });

  /**
   * Undo last change, as long as the table is still at `expected`: a write
   * that landed since (anyone's) refuses it, so it never undoes that write.
   * Nothing to name (no page or delete shown yet): nothing to undo.
   */
  const undoAt = async (expected: number | undefined) => {
    if (!projectId || expected === undefined || expected < 0) return;
    if (undoing.current) return;
    undoing.current = true;
    try {
      const result = await undoLastChange(projectId, frameId, expected);
      // The next Undo names this revision, even if the reload falls behind.
      shown.current.data = Math.max(shown.current.data, result.data_revision);
      setConflict(null);
      tell("undone");
      await load(false);
    } catch (error) {
      const code = error instanceof DatasetError ? error.code : "failed";
      tell(
        code === "not_found"
          ? "nothingToUndo"
          : code === "conflict"
            ? "undoRefused"
            : "saveFailed",
      );
    } finally {
      undoing.current = false;
    }
  };

  /** A field change applied: its fields at once, then the rows it rewrote. */
  const fieldsChanged = (description: DatasetDescription) => {
    if (description.schema_revision > shown.current.schema) {
      shown.current.schema = description.schema_revision;
      setPage((previous) => ({
        ...previous,
        fields: description.fields,
        schemaRevision: description.schema_revision,
      }));
    }
    setConflict(null);
    void load(false);
  };

  return {
    ...page,
    offset,
    setOffset,
    retry: () => {
      setPage((previous) => ({ ...previous, status: "loading" }));
      setAttempt((n) => n + 1);
    },
    fieldsChanged,
    reloadFields: () => void load(true),
    edit,
    addRow,
    deleteRow,
    /** Undo the latest change this page shows. */
    undo: () => undoAt(shown.current.data),
    /** Undo the delete the notice names. */
    undoDelete: () => undoAt(notice?.undoRevision),
    notice: notice?.text ?? null,
    noticeId: notice?.id ?? 0,
    noticeUndoable:
      notice?.undoRevision !== undefined && dataRevision <= notice.undoRevision,
    conflict,
  };
}
